import { useMemo } from 'react';
import { LatLngExpression } from 'leaflet';
import { useQueries } from '@tanstack/react-query';
import { getRoutes } from '../api/routes';

export const ROUTE_COLORS = ['#7c3aed', '#0ea5e9', '#f59e0b', '#10b981', '#ef4444', '#ec4899'];

export function routeColor(index: number): string {
  return ROUTE_COLORS[index % ROUTE_COLORS.length];
}

/**
 * Fetch route geometry (route_lat/route_lon) for the given route ids and return
 * a map of route id -> polyline positions. List payloads (e.g. minimal_route,
 * route_management scopes) omit the geometry arrays, so each route is fetched
 * individually by id and cached indefinitely. Routes without stored geometry
 * are simply absent from the returned map.
 */
export function useRoutePaths(routeIds: string[]): Map<string, LatLngExpression[]> {
  const routeQueries = useQueries({
    queries: routeIds.map((routeId) => ({
      queryKey: ['route-geometry', routeId],
      queryFn: () => getRoutes(`id=${routeId}`).then((routes) => routes[0]),
      staleTime: Infinity,
    })),
  });

  // Stable scalar key: recompute only when a route's geometry arrives or the id
  // list changes, not on every render (query result arrays are new each render).
  const loadedKey = routeQueries.map((query, idx) => (query.data ? routeIds[idx] : '')).join('|');

  return useMemo(() => {
    const paths = new Map<string, LatLngExpression[]>();
    routeQueries.forEach((query, idx) => {
      const route = query.data;
      if (!route?.route_lat || !route?.route_lon) return;
      const positions: LatLngExpression[] = [];
      for (let i = 0; i < route.route_lat.length; i++) {
        const lat = route.route_lat[i];
        const lon = route.route_lon[i];
        if (typeof lat === 'number' && typeof lon === 'number') positions.push([lat, lon]);
      }
      if (positions.length > 1) paths.set(routeIds[idx], positions);
    });
    return paths;
  }, [loadedKey]);
}
